// components/BookCallModal.tsx
// ✅ v5 — הסרת כפתור "המשך לאתר הרשמי בלי להשאיר" ב-mode=official

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TextInput,
  Pressable,
  ActivityIndicator,
  Animated,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { X } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { Course } from '@/types/chat';
import { createClient } from '@supabase/supabase-js';
import { useAccessibility } from '@/contexts/AccessibilityContext';

type Mode = 'lead' | 'official' | 'newsletter';

interface BookCallModalProps {
  visible: boolean;
  course: Course | null;
  onClose: () => void;
  mode?: Mode;
  onSubmitted?: (payload: { leadId?: string; mode: Mode; fullName?: string; phone?: string; email?: string }) => void;
  onSkip?: () => void;
  title?: string;
  subtitle?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  source?: string;
  conversationId?: string;
  sessionId?: string;
  notesPreset?: string;
  hardGate?: boolean;
}

const STORAGE_KEY = '@kursi_lead_gate_draft_v2';

function cleanPhone(phone: string): string {
  return (phone || '').replace(/[-\s]/g, '');
}
function validatePhoneIL(phone: string): boolean {
  const p = cleanPhone(phone);
  return /^05\d{8}$/.test(p);
}
function validateEmail(email: string): boolean {
  const e = (email || '').trim();
  if (!e) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);
}

export function BookCallModal({
  visible,
  course,
  onClose,
  onSubmitted,
  onSkip,
  mode = 'lead',
  title,
  subtitle,
  primaryLabel,
  secondaryLabel,
  source = 'course_proxy',
  conversationId,
  sessionId,
  notesPreset,
  hardGate = false,
}: BookCallModalProps) {
  const { settings } = useAccessibility();
  const firstInputRef = useRef<TextInput>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [freeText, setFreeText] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [isSuccess, setIsSuccess] = useState(false);

  const ui = useMemo(() => {
    if (mode === 'official' && hardGate) {
      return {
        title: title || 'לפני הכניסה לדף הקורס',
        subtitle: subtitle || 'כדי לצפות בפרטי הקורס, צריך להשאיר אימייל תקין.',
        primaryLabel: primaryLabel || 'המשך עם אימייל',
        secondaryLabel: null,
        successTitle: 'נשמר בהצלחה ✅',
        successSubtitle: 'אפשר להמשיך עכשיו לדף הקורס.',
        successCta: 'המשך',
      };
    }
    if (mode === 'official') {
      return {
        title: title || 'לפני המעבר לאתר הרשמי',
        subtitle:
          subtitle ||
          'השאירו אימייל ואנחנו נשלח לכם את הקישור ישירות.',
        primaryLabel: primaryLabel || 'שלח לי את הקישור במייל',
        // ✅ אין secondaryLabel ב-official — הכפתור לא יוצג
        secondaryLabel: null,
        successTitle: 'הקישור בדרך אליכם',
        successSubtitle: 'שלחנו מייל עם הקישור. אם לא הגיע תוך דקה — בדקו ספאם.',
        successCta: 'סגור',
      };
    }
    if (mode === 'newsletter') {
      return {
        title: title || 'רוצה לקבל עדכונים והטבות?',
        subtitle:
          subtitle ||
          'השאירו אימייל ותקבלו עדכונים על קורסים חדשים והטבות למשתמשי קורסי.',
        primaryLabel: primaryLabel || 'הצטרפתי',
        secondaryLabel: secondaryLabel || 'לא עכשיו',
        successTitle: 'נרשמתם בהצלחה',
        successSubtitle: 'קיבלתם אישור. בקרוב תגיעו עדכונים והטבות.',
        successCta: 'סגור',
      };
    }
    return {
      title: title || 'פרטים לקבלת מידע',
      subtitle:
        subtitle ||
        'נבקש רק שם וטלפון כדי שבעל הקורס יחזור אליך עם מחיר, זמינות וכל הפרטים.',
      primaryLabel: primaryLabel || 'שלח פרטים למכללה',
      secondaryLabel: secondaryLabel || 'לא עכשיו',
      successTitle: 'הפרטים נשלחו בהצלחה 🎉',
      successSubtitle: 'נחזור אליך בהקדם עם כל הפרטים.',
      successCta: 'סגור',
    };
  }, [mode, hardGate, title, subtitle, primaryLabel, secondaryLabel]);

  const requirements = useMemo(() => {
    if (mode === 'lead') {
      return { needName: true, needPhone: true, needEmail: false, showPhone: true };
    }
    return { needName: false, needPhone: false, needEmail: true, showPhone: false };
  }, [mode]);

  const canSubmit = useMemo(() => {
    if (requirements.needEmail && !validateEmail(email)) return false;
    if (requirements.needName && fullName.trim() === '') return false;
    if (requirements.needPhone && !validatePhoneIL(phone)) return false;
    return true;
  }, [requirements, email, fullName, phone]);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setIsSubmitting(false);
    setIsSuccess(false);

    (async () => {
      try {
        const draft = await AsyncStorage.getItem(STORAGE_KEY);
        if (draft) {
          const d = JSON.parse(draft);
          setFullName(d.fullName || '');
          setPhone(d.phone || '');
          setEmail(d.email || '');
          setFreeText(d.freeText || '');
        }
      } catch {}
    })();

    Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: true }).start(() => {
      setTimeout(() => firstInputRef.current?.focus?.(), 60);
    });

    return () => { fadeAnim.setValue(0); };
  }, [visible, fadeAnim]);

  useEffect(() => {
    if (!visible) return;
    (async () => {
      try {
        await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify({ fullName, phone, email, freeText }));
      } catch {}
    })();
  }, [visible, fullName, phone, email, freeText]);

  const clearDraft = async () => {
    try { await AsyncStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const handleClose = () => {
    if (hardGate && !isSuccess) return;
    onClose();
  };

  const handleSecondary = () => {
    if (hardGate && !isSuccess) return;
    if (mode === 'official') onSkip?.();
    onClose();
  };

  const handleSubmit = async () => {
    if (!course) return;

    const n = fullName.trim();
    const p = cleanPhone(phone);
    const e = email.trim();

    if (requirements.needName && !n) return setError('צריך שם מלא.');
    if (requirements.needPhone && !validatePhoneIL(p)) return setError('טלפון חייב להתחיל ב-05 ולהכיל 10 ספרות.');
    if (requirements.needEmail && !validateEmail(e)) return setError('אימייל לא תקין.');

    setIsSubmitting(true);
    setError(null);

    try {
      const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
      const supabaseKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) throw new Error('Missing Supabase env');

      const supabase = createClient(supabaseUrl, supabaseKey);

      const meta = {
        mode,
        course_id: course.id,
        course_name: course.name,
        mentor: (course as any)?.mentor || null,
        source,
        conversationId: conversationId || null,
        sessionId: sessionId || null,
      };

      const messageParts: string[] = [];
      if (notesPreset) messageParts.push(notesPreset);
      if (freeText.trim()) messageParts.push(`הערות משתמש: ${freeText.trim()}`);
      messageParts.push(`meta: ${JSON.stringify(meta)}`);

      const { error: insertError } = await supabase.from('leads').insert({
        full_name: n || 'לא צוין',
        name: n || null,
        phone: requirements.needPhone ? p : p || '0000000000',
        email: e || null,
        course_id: course.id || 'unknown',
        status: 'new',
        source: source || 'website',
        message: messageParts.join('\n\n'),
        notes: freeText.trim() || null,
      });

      if (insertError) throw insertError;

      await clearDraft();

      onSubmitted?.({
        mode,
        fullName: n || undefined,
        phone: requirements.needPhone ? p : undefined,
        email: e || undefined,
      });

      setIsSuccess(true);

      // ✅ AUTO-REDIRECT: אם זה mode='official' ויש onSkip, קפוץ אוטומטית לאתר
      if (mode === 'official' && onSkip) {
        setTimeout(() => {
          onSkip();
          onClose();
        }, 1500); // המתן 1.5 שניות להצגת ההודעה
      }
    } catch {
      setError('משהו השתבש בשליחה. נסה שוב בעוד רגע.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!course) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      accessibilityViewIsModal
    >
      <View style={styles.overlay}>
        <Animated.View style={[styles.modal, { opacity: fadeAnim }]}>
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>{isSuccess ? ui.successTitle : ui.title}</Text>
              <Text style={styles.subtitle}>{isSuccess ? ui.successSubtitle : ui.subtitle}</Text>
            </View>
            {(!hardGate || isSuccess) ? (
              <Pressable
                onPress={handleClose}
                style={({ focused }) => [
                  styles.closeBtn,
                  settings.keyboardNavigation && focused && styles.focusOutline,
                ]}
                accessibilityRole="button"
                accessibilityLabel="סגור"
              >
                <X size={22} color={theme.colors.textLight} />
              </Pressable>
            ) : null}
          </View>

          {isSuccess ? (
            <View style={styles.footer}>
              <Pressable
                onPress={handleClose}
                style={styles.primaryBtn}
                accessibilityRole="button"
                accessibilityLabel={ui.successCta}
              >
                <Text style={styles.primaryText}>{ui.successCta}</Text>
              </Pressable>
            </View>
          ) : (
            <>
              <View style={styles.form}>
                {(requirements.needName || mode === 'lead') ? (
                  <>
                    <Text style={styles.label}>שם מלא{requirements.needName ? ' *' : ''}</Text>
                    <TextInput
                      ref={firstInputRef}
                      style={styles.input}
                      value={fullName}
                      onChangeText={setFullName}
                      placeholder="הכנס שם מלא"
                      placeholderTextColor={theme.colors.textLight}
                      textAlign="right"
                    />
                  </>
                ) : (
                  <TextInput
                    ref={firstInputRef}
                    style={{ height: 0, width: 0, opacity: 0 }}
                    value=""
                    onChangeText={() => {}}
                  />
                )}

                {requirements.showPhone ? (
                  <>
                    <Text style={styles.label}>טלפון *</Text>
                    <TextInput
                      style={styles.input}
                      value={phone}
                      onChangeText={setPhone}
                      placeholder="05X-XXXXXXX"
                      placeholderTextColor={theme.colors.textLight}
                      keyboardType="phone-pad"
                      textAlign="right"
                    />
                  </>
                ) : null}

                <Text style={styles.label}>
                  אימייל{requirements.needEmail ? ' *' : ' (אופציונלי)'}
                </Text>
                <TextInput
                  style={styles.input}
                  value={email}
                  onChangeText={setEmail}
                  placeholder={requirements.needEmail ? 'name@email.com' : 'כדי לקבל עדכונים והטבות'}
                  placeholderTextColor={theme.colors.textLight}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  textAlign="right"
                />

                {mode === 'lead' ? (
                  <>
                    <Text style={styles.label}>הערה לבעל הקורס (אופציונלי)</Text>
                    <TextInput
                      style={[styles.input, styles.textArea]}
                      value={freeText}
                      onChangeText={setFreeText}
                      placeholder="למשל: מתי מתחילים? יש תעודה?"
                      placeholderTextColor={theme.colors.textLight}
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                      textAlign="right"
                    />
                  </>
                ) : null}

                {error ? <Text style={styles.error}>{error}</Text> : null}
              </View>

              <View style={styles.footer}>
                <Pressable
                  onPress={handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                  style={[styles.primaryBtn, (!canSubmit || isSubmitting) && styles.primaryBtnDisabled]}
                  accessibilityRole="button"
                  accessibilityLabel={ui.primaryLabel}
                >
                  {isSubmitting ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={styles.primaryText}>{ui.primaryLabel}</Text>
                  )}
                </Pressable>

                {/* ✅ כפתור משני מוצג רק אם יש secondaryLabel (לא ב-official) */}
                {!hardGate && ui.secondaryLabel ? (
                  <Pressable
                    onPress={handleSecondary}
                    style={styles.secondaryBtn}
                    accessibilityRole="button"
                    accessibilityLabel={ui.secondaryLabel}
                  >
                    <Text style={styles.secondaryText}>{ui.secondaryLabel}</Text>
                  </Pressable>
                ) : null}

                <Text style={styles.disclaimer}>
                  בלחיצה על שליחה אתה מאשר/ת העברת פרטים לצורך יצירת קשר ועדכונים.
                </Text>
              </View>
            </>
          )}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 18,
  },
  modal: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: theme.colors.background,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row-reverse',
    gap: 10,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    alignItems: 'flex-start',
  },
  closeBtn: { padding: 6 },
  title: {
    fontFamily: theme.fonts.bold,
    fontSize: 18,
    color: theme.colors.text,
    textAlign: 'right',
    marginBottom: 4,
  },
  subtitle: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: theme.colors.textLight,
    textAlign: 'right',
    lineHeight: 18,
  },
  form: { padding: 16, gap: 10 },
  label: {
    fontFamily: theme.fonts.semiBold,
    fontSize: 13,
    color: theme.colors.text,
    textAlign: 'right',
    marginTop: 4,
  },
  input: {
    backgroundColor: theme.colors.secondary,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    color: theme.colors.text,
  },
  textArea: { minHeight: 96 },
  error: {
    marginTop: 6,
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: theme.colors.error,
    textAlign: 'right',
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    gap: 10,
  },
  primaryBtn: {
    backgroundColor: theme.colors.primary,
    paddingVertical: 13,
    borderRadius: 12,
    alignItems: 'center',
  },
  primaryBtnDisabled: { opacity: 0.55 },
  primaryText: { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff' },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  secondaryText: { fontFamily: theme.fonts.bold, fontSize: 14, color: theme.colors.text },
  disclaimer: {
    fontFamily: theme.fonts.regular,
    fontSize: 11,
    color: theme.colors.textLight,
    textAlign: 'right',
    lineHeight: 16,
  },
  focusOutline: {
    borderWidth: 2,
    borderColor: theme.colors.primary,
    borderRadius: 10,
  },
});
